import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    CHART_HEIGHT,
    ChartEmpty,
    ChartLegend,
    ChartTooltipFrame,
    formatBucketTick,
    formatBucketTitle,
} from '@apps/web/components/admin/llm_spend/chart_shared';
import {
    type SpendBucketSize,
    type SpendSeries,
} from '@apps/web/components/admin/llm_spend/constants';

export type LinePoint = {
    readonly bucketStart: string;
    // Null breaks the line for a bucket with no denominator rather than
    // dropping it to a misleading zero.
    readonly values: Readonly<Record<string, number | null>>;
};

type LineRow = {
    readonly bucketStart: string;
    readonly [seriesKey: string]: number | string | null;
};

function LineTooltip({
    active,
    label,
    payload,
    series,
    bucket,
    valueFormatter,
}: {
    readonly active?: boolean;
    readonly label?: string;
    readonly payload?: Array<{ readonly payload?: LineRow }>;
    readonly series: ReadonlyArray<SpendSeries>;
    readonly bucket: SpendBucketSize;
    readonly valueFormatter: (value: number) => string;
}) {
    const row = payload?.[0]?.payload;
    if (!active || label === undefined || row === undefined) {
        return null;
    }
    return (
        <ChartTooltipFrame
            title={formatBucketTitle(label, bucket)}
            rows={series.map((entry) => {
                const raw = row[entry.key];
                return {
                    key: entry.key,
                    label: entry.label,
                    color: entry.color,
                    value: typeof raw === 'number' ? valueFormatter(raw) : '—',
                };
            })}
        />
    );
}

// One or more lines that share a unit and therefore one y-axis. Two measures
// with different units get two charts, never a second axis.
export function SpendLineChart({
    points,
    series,
    bucket,
    valueFormatter,
    yDomain,
}: {
    readonly points: ReadonlyArray<LinePoint>;
    readonly series: ReadonlyArray<SpendSeries>;
    readonly bucket: SpendBucketSize;
    readonly valueFormatter: (value: number) => string;
    readonly yDomain?: [number, number];
}) {
    if (points.length === 0) {
        return <ChartEmpty />;
    }
    const rows: Array<LineRow> = points.map((point) => ({
        bucketStart: point.bucketStart,
        ...Object.fromEntries(
            series.map((entry) => [entry.key, point.values[entry.key] ?? null]),
        ),
    }));
    return (
        <div className="flex flex-col gap-2">
            <ChartLegend series={series} />
            <div className="w-full" style={{ height: CHART_HEIGHT }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={rows}
                        margin={{ top: 4, left: 0, right: 8, bottom: 0 }}
                    >
                        <CartesianGrid
                            stroke="var(--border)"
                            vertical={false}
                        />
                        <XAxis
                            dataKey="bucketStart"
                            tick={{ fontSize: 11 }}
                            tickLine={false}
                            axisLine={{ stroke: 'var(--border)' }}
                            tickFormatter={formatBucketTick}
                            interval="preserveStartEnd"
                            minTickGap={28}
                        />
                        <YAxis
                            tick={{ fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                            width={52}
                            domain={yDomain ?? [0, 'auto']}
                            tickFormatter={valueFormatter}
                        />
                        <RechartsTooltip
                            cursor={{ stroke: 'var(--border)' }}
                            content={
                                <LineTooltip
                                    series={series}
                                    bucket={bucket}
                                    valueFormatter={valueFormatter}
                                />
                            }
                        />
                        {series.map((entry) => (
                            <Line
                                key={entry.key}
                                type="monotone"
                                dataKey={entry.key}
                                name={entry.label}
                                stroke={entry.color}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4, strokeWidth: 0 }}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
